import type { TLocale } from 'i18n/constants'

import { SOCIAL_NAME } from 'modules/auth/constants'
import { SHARE_TYPES } from 'modules/share/constants'

export const QA = {
  ELEMENTS: {
    LABEL: 'elements-label',
    LOCATION_INPUT: {
      POPOVER: 'location-input-popover',
      OPTION: 'location-input-option',
    },
  },
  COMMON: {
    LOCALE_TOGGLE_BUTTON: 'locale-toggle-button',
    LOCALES_WRAPPER: 'locales-wrapper',
    LOCALE_ITEM_BUTTON: (locale: TLocale) => `locale-item-${locale.toLowerCase()}-button`,
    ALERT_CLOSE_BUTTON: 'alert-close-button',
    HELP_LINK: 'help-link',
    FLASH_MESSAGE: 'flash-message',
    BREADCRUMB_BUTTON: 'breadcrumb-button',
    BREADCRUMB_CONTENT: 'breadcrumb-content',
    ROUNDED_SEARCH_INPUT: 'rounded-search-input',
    RADIO_INPUT: 'radio-input',
    RADIO_INPUT_LABEL: 'radio-input-label',
    INPUT_FILE: 'input-file',
    DIV_FILE: 'div-file',
    VALIDATE_FILE_BUTTON: 'validate-file-button',
    ERROR_MESSAGE_UPLOAD: 'error-message-upload',
    FROALA_EDITOR: 'froala-editor',
    DATE_PICKER: 'date-picker',
    TAB_LOGIN_LINK: 'tab-login-link',
    AVATAR_IMAGE: 'avatar-image',
    PRAY_BUTTON: 'pray-button',
    BANNER: {
      PUBLICATION: {
        WRAPPER: 'banner-publication-wrapper',
        CLOSE_BUTTON: 'banner-publication-close',
      },
      COOKIES: {
        WRAPPER: 'banner-accept-cookies',
        ACCEPT_BUTTON: 'banner-accept-cookies-button',
        CLOSE_BUTTON: 'banner-close-cookies-button',
      },
      LANG: {
        WRAPPER: 'banner-lang-wrapper',
        CHOOSE_SUGGESTED_BUTTON: 'banner-lang-choose-selected',
        CLOSE_BUTTON: 'banner-lang-close',
      },
    },
    TAB_LINK: (tabId: string) => `tab-${tabId}-link`,
    SIDEBAR: 'sidebar',
    HEADER: {
      LOGIN_BUTTON: 'header-login-button',
      SIGNUP_BUTTON: 'header-signup-button',
      NAVIGATION_OPEN_BUTTON: 'header-navigation-open-button',
      LOGO_BUTTON: 'header-logo-button',
      DONATE_BUTTON: 'header-donate-button',
      FEED_PAGE_LINK: 'header-feed-page-link',
      COMMUNITIES_PAGE_LINK: 'header-communities-page-link',
      MESSAGES_PAGE_LINK: 'header-messages-page-link',
    },
    NAVIGATION: {
      CLOSE_BUTTON: 'navigation-close-button',
      NAVIGATION_PANEL: 'navigation-panel',
      DONATE_LINK: 'navigation-donate-link',
      HOME_LINK: 'navigation-home-button',
      COMMUNITIES_LINK: 'navigation-discover-communities-link',
      PROFILE_LINK: 'navigation-profile-link',
      FAVORITES_LINK: 'navigation-favorites-link',
      WIDGET_LINK: 'navigation-widget-link',
      SETTINGS_LINK: 'navigation-settings-link',
      LIVE_LINK: 'navigation-live-link',
      LOGIN_LINK: 'navigation-login-link',
      SIGNUP_LINK: 'navigation-signup-link',
      APP_STORE_LINK: 'navigation-app-store-link',
      MISC_LINKS: (name: string) => `navigation-misc-${name}-link`,
      SOCIAL_LINK: (name: string) => `navigation-social-${name}-link`,
    },
    POPIN: {
      POPIN_WRAPPER: 'popin-wrapper',
      POPIN_BACK: 'popin-wrapper-back',
    },
    CONFIRM_POPIN: {
      CONFIRM_BUTTON: 'confirm-popin-confirm-button',
      CANCEL_BUTTON: 'confirm-popin-cancel-button',
      MESSAGE: 'confirmation-popin-message',
    },
    INPUT_POPIN: {
      FORM: 'input-popin-form',
      INPUT: 'input-popin-form-input',
      CONFIRM: 'input-popin-form-confirm',
      CANCEL: 'input-popin-form-cancel',
    },
    MESSAGE_POPIN: {
      MESSAGE: 'message-popin-message',
      BUTTON: 'message-popin-button',
    },
    PUSH: {
      ASK: 'push-ask',
      PUSH_ASK_ACCEPT: 'push-ask-accept',
      PUSH_ASK_REFUSE: 'push-ask-refuse',
      MESSAGE: 'push-message',
    },
    PUSH_BLOCKED_POPIN: {
      ENABLE_NOTIFICATIONS_BUTTON: 'push-blocked-popin-enable-notifications-button',
      NO_THANKS_BUTTON: 'push-blocked-popin-no-thanks-button',
      CONFIRM_BUTTON: 'push-blocked-popin-confirm-button',
    },
    TUTORIAL_POPIN: {
      CHAPTER: 'tutorial-popin-chapter',
      NEXT_BUTTON: 'tutorial-popin-next-button',
    },
    LINUX_DEV_POPIN: {
      TEXT: 'linux-dev-popin-text',
      BUTTON: 'linux-dev-popin-button',
    },
    APP_PROMPT_POPIN: {
      STORE_LINK: 'app-prompt-popin-store-link',
    },
    RATE_PROMPT_POPIN: {
      CONFIRM_BUTTON: 'rate-prompt-popin-confirm-button',
      REFUSE_BUTTON: 'rate-prompt-popin-refuse-button',
    },
  },
  AUTH: {
    EMAIL: {
      INPUT: 'auth-email-input',
      SUBMIT_BUTTON: 'auth-email-submit-button',
    },
    PASSWORD: {
      INPUT: 'auth-password-input',
      SUBMIT_BUTTON: 'auth-password-submit-button',
    },
    NAME: {
      INPUT: 'auth-name-input',
      SUBMIT_BUTTON: 'auth-name-submit-button',
    },
    SOCIAL_CONNECT_BUTTON: (socialName: SOCIAL_NAME) => `social-connect-${socialName}-button`,
    CAPTCHA: 'contact-page-captcha',
  },
  COMMUNITY_COMMON: {
    DATES: 'community-dates',
    NB_MEMBERS: 'community-nb-members',
    JOIN_BUTTON: 'community-join-button',
    LEAVE_BUTTON: 'community-leave-button',
    JOIN_POPIN_FORM: 'community-join-popin-form',
    JOIN_POPIN_DATE_INPUT: 'community-join-popin-date-input',
    JOIN_POPIN_DATE_BUTTON: 'community-join-date-button',
    JOIN_POPIN_TITLE: 'community-join-popin-title',
    JOIN_POPIN_VALIDATE_DATE: 'community-join-validate-date',
    JOIN_POPIN_ONGOING: 'community-join-popin-ongoing',
    JOINED_POPIN_EMAIL_ACCEPT_BUTTON: 'community-joined-email-accept',
    JOINED_POPIN_EMAIL_REFUSE_BUTTON: 'community-joined-email-refuse',
    JOINED_POPIN_SHARE_COPY_LINK: 'community-joined-share-copy-link',
    JOINED_POPIN_SHARE_BY_MAIL: 'community-joined-share-by-mail',
    JOINED_POPIN_VALIDATE_PROFILE: 'community-joined-validate-profile',
    JOINED_POPIN_CLOSE: 'community-joined-close',
    LEAVE_POPIN_CONFIRM_BUTTON: 'community-leave-popin-confirm',
    LEAVE_POPIN_CANCEL_BUTTON: 'community-leave-popin-cancel',
    NOTIFICATION_PUSH: 'community-notification-popin-push',
    NOTIFICATION_EMAIL_PUBLICATIONS: 'community-notification-popin-email-publication',
    NOTIFICATION_EMAIL_ANNOUNCEMENTS: 'community-notification-popin-email-annoucements',
    NOTIFICATION_BUTTON: 'community-notifications-button',
    SHOW_ACTIONS_BUTTON: 'community-show-actions-button',
    ADMIN_BUTTON: 'community-admin-button',
    COMMUNITY_THUMBNAIL: 'community-thumbnail',
    SHARE_BUTTON: 'community-share-button',
    START_TODAY_BUTTON: 'community-start-today-button',
    SUBMITTED_POPIN_EXPLANATIONS_TEXT: 'community-submitted-popin-explanations-text',
    SUBMITTED_POPIN_CONFIRM_BUTTON: 'community-submitted-popin-confirm-button',
    SUBMITTED_POPIN_I_UNDERSTAND_BUTTON: 'community-submitted-popin-i-understand-button',
    COMMUNITY_MEMBERS_LIST_POPIN: {
      USERS_LIST: 'community-members-list-popin-users-list',
    },
  },
  COMMUNITY_WIDGET: {
    JOIN_BUTTON: 'community-widget-join-button',
    TABS_BUTTON: (i: number) => `community-widget-tabs-button-${i}`,
  },
  SHARE_COMMON: {
    SOCIAL_BUTTON: (shareType: SHARE_TYPES) => `share-social-button-${shareType}`,
    NB_SHARES: 'share-nb-shares',
    INVITE_BUTTON: 'share-invite-button',
    COPY_MAIL_BUTTON: 'share-copy-mail-button',
    COPY_LINK_BUTTON: 'share-copy-link-button',
  },
  USER_COMMON: {
    AVATAR: `user-avatar`,
    NAME: `user-name`,
  },
  RESET_PASSWORD_PAGE: {
    PASSWORD_FORM: 'reset-password-password-form',
  },
  HOMEPAGE: {
    TOP_SECTION: (name: string) => `homepage-top-section-${name}`,
    CREATOR_SECTION_IMAGES: 'homepage-creator-section-images',
    CREATOR_SECTION_USERS: 'homepage-creator-section-users',
    TAGS_SECTION: 'homepage-tags-section',
  },
  COMMUNITY_PAGE: {
    TITLE: 'community-page-title',
    INTRO: 'community-page-intro',
    IMAGE: 'community-page-image',
    TABS_BAR: 'community-page-tabs-bar',
    TABS: <TL extends Record<string, string>>(tabList: TL): TL =>
      Object.keys(tabList).reduce<TL>(
        (acc, tabName: string) => ({
          ...acc,
          [tabName]: `tab-${tabName}-link`,
        }),
        {} as TL,
      ),
    SHARE_BLOCK: 'community-page-share-block',
    PRAYER_BLOCK: 'community-page-prayer-block',
    ACTION_BUTTONS: 'community-page-action-buttons',
    PUBLICATIONS: {
      TAB: 'tab-publications-link',
      WRAPPER: 'community-page-publications-wrapper',
    },
    SHARE_POPIN: {
      COPY_BUTTON: 'share-community-copy-button',
    },
    DESCRIPTION: {
      TAB: 'tab-description-link',
      WRAPPER: 'community-page-description-wrapper',
      TAG: 'community-page-tag',
      CONTENT: 'community-page-content',
      PRAYER: 'community-page-prayer',
      MOST_COMMENTED_PUBLICATIONS: 'community-page-most-commented-publications',
      COMMUNITIES_PROPOSITIONS: 'community-page-communities-propositions',
      GOOGLE_MAP: 'community-page-google-map',
    },
    SIDEBAR: {
      MAIN_BLOCK: 'community-page-sidebar-main-block',
      ADMIN_BLOCK: 'community-page-sidebar-admin-block',
    },
    SUPER_ADMIN_ACTIONS: {
      ADD_TAGS_BUTTON: 'community-page-add-tags-button',
      DISPLAY_MODE_BUTTON: 'community-display-mode-button',
      LIVE_FORM: 'community-live-form',
    },
    PRAYER: 'community-page-prayer',
  },
  INTENTION: {
    FORM: 'intention-form',
    OPTIONS: 'intention-options',
    EDIT: 'intention-edit',
    REMOVE: 'intention-remove',
    SHARE: 'intention-share',
    NAME_INPUT: 'intention-name-input',
    EMAIL_INPUT: 'intention-email-input',
    CONTENT_INPUT: 'intention-content-input',
    YOUR_INTENTION: 'intention-your-intention',
    LIST_INTENTION_ITEM: 'intention-list-intention-item',
    LIST_ARRIVAL_ITEM: 'intention-list-arrival-item',
    PRAY_BUTTON: 'intention-pray-button',
    PANEL: 'intention-panel',
    PANEL_TOGGLE: 'intention-panel-toggle',
    PRAYER_LIST_LINK: 'intention-prayers-list-link',
    PUBLISH_BUTTON: 'intention-publish-button',
    BROADCAST_POPIN: {
      POPIN: 'intention-broadcast-popin',
      NO: 'intention-broadcast-popin-no',
      YES: 'intention-broadcast-popin-yes',
    },
    SUCCESS_POPIN: {
      POPIN: 'intention-success-popin',
      NO: 'intention-success-popin-no',
      YES: 'intention-success-popin-yes',
      CONFIRM: 'intention-success-popin-confirm',
    },
    BROADCAST_INTENTION_WIDGET_POPIN: {
      BROADCAST_CHECKBOX: 'broadcast-intention-widget-popin-broadcast-checkbox',
      NEXT_BUTTON: 'broadcast-intention-widget-popin-next-button',
    },
    VALIDATION_INTENTION_WIDGET_POPIN: {
      CHECK_ICON: 'validation-intention-widget-popin-check-icon',
      UNDERSTOOD: 'validation-intention-widget-popin-understood',
    },
  },
  PRAY: {
    AMEN_BUTTON: 'pray-amen-button',
    PRAYED_BUTTON: 'prayed-amen-button',
    PUBLICATION_PRAY_SUCCESS_POPIN: 'publication-pray-success-popin',
    USER_PRAY_POPIN: {
      CANDLE: 'user-pray-popin-candle',
    },
  },
  COMMUNITY_LIST: {
    PAGE_TITLE: 'communitylist-page-title',
    LABEL: 'communitylist-label',
  },
  LOST_PWD_POPIN: {
    EMAIL_INPUT: 'lost-pwd-popin-email-input',
    RESET_PASSWORD_BUTTON: 'lost-pwd-popin-reset-password-button',
    AUTH_RECOVER_SENT: 'lost-pwd-popin-auth-recover-sent',
  },
} as const
